if (!customElements.get('sticky-atc')) {
  class StickyATC extends HTMLElement {
    constructor() {
      super();
      this.buyBox = null;
      this.observer = null;
      this.onPurchaseState = this.onPurchaseState.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
    }

    connectedCallback() {
      this.buyBox = document.querySelector('[data-lt-purchase-box]');
      if (!this.buyBox) return;

      this.action = this.querySelector('[data-sticky-action]');
      this.price = this.querySelector('[data-sticky-price]');
      this.meta = this.querySelector('[data-sticky-meta]');

      // Phones: until the visitor taps a size, the bar shows from the first screen as
      // "Choose size" and scrolls to the size step instead of submitting the default size.
      // Clarity 2026-09-25: ~half of phone visitors left the PDP before the buy button was on
      // screen (button at ~1,500 px). See metrics/predictions/pdp-mobile-sticky-2026-09-25.json.
      this.phone = window.matchMedia('(max-width: 749px)');
      this.sizeChosen = false;
      this.buyBox.addEventListener('click', (event) => {
        if (!event.target.closest('.subscribe-box__weight-btn')) return;
        this.sizeChosen = true;
        this.updateVisibility();
      });

      document.addEventListener('longtails:purchase-state', this.onPurchaseState);
      this.action?.addEventListener('click', () => {
        if (this.chooseMode) {
          if (window.clarity) window.clarity('event', 'sticky_choose_size');
          this.buyBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (window.clarity) window.clarity('event', 'sticky_submit');
        const mode = this.buyBox.dataset.ltMode;
        const target = mode === 'onetime'
          ? this.buyBox.querySelector('.subscribe-box__cta--one')
          : this.buyBox.querySelector('[data-subscribe-cta]');

        if (target) {
          target.click();
          return;
        }

        this.buyBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      this.syncFromBuyBox();

      this.observer = new IntersectionObserver(this.onViewportChange, { threshold: 0 });

      this.observer.observe(this.buyBox);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange);
      this.updateVisibility();
    }

    disconnectedCallback() {
      document.removeEventListener('longtails:purchase-state', this.onPurchaseState);
      if (this.observer) {
        this.observer.disconnect();
      }
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
    }

    onPurchaseState(event) {
      const state = event.detail;
      if (!state) return;

      if (this.price) this.price.textContent = state.price;
      if (this.meta) {
        const jars = `${state.quantity} ${state.quantity === 1 ? 'jar' : 'jars'}`;
        this.meta.textContent = `${state.sizeLabel} dog · ${jars} · ${state.supplyDays} days`;
      }
      this.actionLabel = state.mode === 'subscribe' ? 'Start routine' : 'Add to cart';
      this.metaLabel = this.meta ? this.meta.textContent : '';

      this.updateVisibility();
    }

    onViewportChange() {
      this.updateVisibility();
    }

    updateVisibility() {
      if (!this.buyBox) return;

      const mode = this.buyBox.dataset.ltMode;
      const purchaseAction = mode === 'onetime'
        ? this.buyBox.querySelector('.subscribe-box__cta--one')
        : this.buyBox.querySelector('[data-subscribe-cta]');
      const target = purchaseAction || this.buyBox;
      const targetRect = target.getBoundingClientRect();
      const buyBoxRect = this.buyBox.getBoundingClientRect();
      const visibleActionHeight = Math.max(
        0,
        Math.min(targetRect.bottom, window.innerHeight) - Math.max(targetRect.top, 0)
      );
      const actionInViewport = visibleActionHeight >= Math.min(targetRect.height * 0.8, 44);
      const visiblePurchaseHeight = Math.max(
        0,
        Math.min(buyBoxRect.bottom, window.innerHeight) - Math.max(buyBoxRect.top, 0)
      );
      const purchaseControlsInViewport = visiblePurchaseHeight >= 48;
      const passedAction = targetRect.bottom <= 0;

      this.chooseMode = Boolean(this.phone?.matches && !this.sizeChosen && buyBoxRect.top > window.innerHeight * 0.6);
      if (this.action) this.action.textContent = this.chooseMode ? 'Choose size' : (this.actionLabel || this.action.textContent);
      if (this.meta && this.metaLabel) this.meta.textContent = this.chooseMode ? 'Pick your dog\u2019s size to start' : this.metaLabel;

      this.classList.toggle(
        'sticky-atc--visible',
        this.chooseMode || passedAction || (purchaseControlsInViewport && !actionInViewport)
      );
    }

    syncFromBuyBox() {
      if (!this.buyBox?.dataset.ltSizeLabel) return;
      this.onPurchaseState({
        detail: {
          sizeLabel: this.buyBox.dataset.ltSizeLabel,
          quantity: Number(this.buyBox.dataset.ltQuantity),
          supplyDays: Number(this.buyBox.dataset.ltSupplyDays),
          mode: this.buyBox.dataset.ltMode,
          price: this.buyBox.dataset.ltPrice,
        },
      });
    }
  }

  customElements.define('sticky-atc', StickyATC);
}
